import type { BomAnswer, ContextQuestion } from './useContextStep';

interface AnalysisRunLike {
  missingFields?: ContextQuestion[];
  detectedRobotCategory?: {
    category?: string | null;
    confirmedCategory?: string | null;
  } | null;
}

/**
 * The context questions as step 3a renders them.
 *
 * The robot-category question asserts a detection in its own copy ("We detected
 * this BOM is for a Drone"), so it must arrive with that value selected. The
 * detection also lives on the analysis run, and runs created before the question
 * itself persisted `detectedValue` only have it there — so the question inherits
 * it from the run. Nothing is invented: with no detection on either the question
 * or the run, the question renders unanswered (and the backend only asks it when
 * it detected something).
 */
export const toContextQuestions = (analysis: AnalysisRunLike | null | undefined): ContextQuestion[] => {
  const questions = analysis?.missingFields || [];
  const detected =
    analysis?.detectedRobotCategory?.confirmedCategory || analysis?.detectedRobotCategory?.category || null;

  if (!detected) return questions;

  return questions.map((q) =>
    q.field === 'robotCategory' && !q.detectedValue
      ? { ...q, isRobotCategoryConfirmation: true, detectedValue: detected }
      : q,
  );
};

/** One component's open questions, under its name. */
export interface ComponentQuestionGroup {
  componentKey: string;
  componentName: string;
  componentIndex: number;
  questions: ContextQuestion[];
}

/**
 * The open questions as the step lays them out: the BOM-wide ones first (the
 * robot category), then one group per component in BOM order. A run analysed
 * before questions were built per row has no components on its questions, so
 * every question stays BOM-wide and the step renders as it always did.
 */
export const groupByComponent = (
  questions: ContextQuestion[],
): { bomWide: ContextQuestion[]; components: ComponentQuestionGroup[] } => {
  const bomWide: ContextQuestion[] = [];
  const groups = new Map<string, ComponentQuestionGroup>();
  questions.forEach((q) => {
    if (!q.componentKey) {
      bomWide.push(q);
      return;
    }
    const group = groups.get(q.componentKey) || {
      componentKey: q.componentKey,
      componentName: q.componentName || 'Component',
      componentIndex: q.componentIndex ?? Number.MAX_SAFE_INTEGER,
      questions: [],
    };
    group.questions.push(q);
    groups.set(q.componentKey, group);
  });
  const components = Array.from(groups.values()).sort((a, b) => a.componentIndex - b.componentIndex);
  return { bomWide, components };
};

/** The BOM's own answers, one list per component in BOM order. */
export const bomAnswersByComponent = (
  answers: BomAnswer[],
): Array<{ componentKey: string; componentName: string; componentIndex: number; answers: BomAnswer[] }> => {
  const groups = new Map<string, { componentKey: string; componentName: string; componentIndex: number; answers: BomAnswer[] }>();
  answers.forEach((a) => {
    const group = groups.get(a.componentKey) || {
      componentKey: a.componentKey,
      componentName: a.componentName,
      componentIndex: a.componentIndex,
      answers: [],
    };
    group.answers.push(a);
    groups.set(a.componentKey, group);
  });
  return Array.from(groups.values()).sort((a, b) => a.componentIndex - b.componentIndex);
};

/** One component as the step renders it: its open questions, then what the BOM already said about it. */
export interface ComponentSection {
  componentKey: string;
  componentName: string;
  componentIndex: number;
  questions: ContextQuestion[];
  bomAnswers: BomAnswer[];
}

/**
 * The step's layout: BOM-wide questions first, then one section per component
 * in BOM order carrying both its open questions and the answers Robbie read off
 * the BOM — so the buyer reviews a component in one place. A component the BOM
 * fully answered still gets a section, holding only its read answers.
 */
export const componentSections = (
  questions: ContextQuestion[],
  bomAnswers: BomAnswer[],
): { bomWide: ContextQuestion[]; components: ComponentSection[] } => {
  const { bomWide, components: asked } = groupByComponent(questions);
  const sections = new Map<string, ComponentSection>();
  asked.forEach((g) => sections.set(g.componentKey, { ...g, bomAnswers: [] }));
  bomAnswersByComponent(bomAnswers).forEach(({ answers, ...component }) => {
    const section = sections.get(component.componentKey) || { ...component, questions: [], bomAnswers: [] };
    section.bomAnswers = answers;
    sections.set(component.componentKey, section);
  });
  const components = Array.from(sections.values()).sort((a, b) => a.componentIndex - b.componentIndex);
  return { bomWide, components };
};

export default toContextQuestions;
